import { Card, CardContent } from "@/components/ui/card";
import { Briefcase, Users, TrendingUp, Video, Target, Zap } from "lucide-react";
import { useEffect, useRef, useState } from "react";

const audiences = [
  {
    icon: Briefcase,
    title: "Business Owners",
    description: "Scale your business with effective online selling strategies and turn your vision into a thriving digital empire",
    pattern: "diagonal-lines",
  },
  {
    icon: Users,
    title: "Aspiring Social Media Managers",
    description: "Launch your career with professional-grade skills that agencies and brands actively seek",
    pattern: "dots",
  },
  {
    icon: TrendingUp,
    title: "Freelancers",
    description: "Add high-income digital marketing skills to your portfolio and command premium rates",
    pattern: "grid",
  },
  {
    icon: Video,
    title: "Content Creators",
    description: "Transform your creative passion into consistent revenue streams with strategic monetization",
    pattern: "circles",
  },
  {
    icon: Target,
    title: "Online Sellers",
    description: "Master the art of converting engaged followers into loyal, paying customers at scale",
    pattern: "waves",
  },
  {
    icon: Zap,
    title: "Career Switchers",
    description: "Break into the high-demand digital marketing industry with confidence and marketable expertise",
    pattern: "triangles",
  },
];

export function AudienceSection() {
  const [visibleCards, setVisibleCards] = useState<number[]>([]);
  const cardsRef = useRef<(HTMLDivElement | null)[]>([]);

  useEffect(() => {
    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) {
            const index = Number(entry.target.getAttribute('data-index'));
            setVisibleCards((prev) => [...prev, index]);
          }
        });
      },
      { threshold: 0.2 }
    );

    cardsRef.current.forEach((card) => {
      if (card) observer.observe(card);
    });

    return () => observer.disconnect();
  }, []);

  return (
    <section className="relative min-h-screen overflow-hidden bg-gradient-to-b from-[hsl(0,100%,8%)] via-[hsl(0,90%,10%)] to-[hsl(0,100%,8%)] py-12 md:py-16 lg:py-20">
      {/* Premium Background Pattern - Dark Maroon Theme */}
      <div className="absolute inset-0 overflow-hidden pointer-events-none">
        <div className="absolute top-0 right-1/2 translate-x-1/2 w-[800px] h-[800px] bg-gradient-radial from-maroon/10 via-transparent to-transparent blur-3xl" />
        <div className="absolute bottom-0 left-0 w-[600px] h-[600px] bg-gradient-radial from-turquoise/10 via-transparent to-transparent blur-3xl" />
        
        {/* Grid Pattern */}
        <div className="absolute inset-0 bg-[linear-gradient(rgba(255,255,255,0.02)_1px,transparent_1px),linear-gradient(90deg,rgba(255,255,255,0.02)_1px,transparent_1px)] bg-[size:50px_50px]" />
      </div>

      <div className="container relative z-10 px-4 md:px-6">
        {/* Section Header */}
        <div className="text-center max-w-3xl mx-auto mb-12 md:mb-16">
          <div className="inline-flex items-center gap-2 px-3 py-1.5 bg-gradient-to-r from-turquoise/20 to-maroon/20 rounded-full border border-turquoise/30 mb-4 md:mb-6">
            <Users className="w-3 h-3 text-turquoise fill-turquoise" />
            <span className="text-xs font-semibold text-white">Target Audience</span>
          </div>
          
          <h2 className="font-heading text-3xl sm:text-4xl md:text-5xl mb-4 md:mb-6 leading-tight text-white">
            This Training Is{" "}
            <span className="italic font-light text-white">
              Designed
            </span>{" "}
            For
          </h2>
          
          <p className="text-gray-300 text-sm md:text-base leading-relaxed max-w-2xl mx-auto">
            Whether you're building a business, starting a career, or pivoting to digital marketing—this program gives you the strategic edge you need.
          </p>
        </div>

        {/* Audience Cards Grid */}
        <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-4 md:gap-5">
          {audiences.map((audience, index) => (
            <div
              key={audience.title}
              ref={(el) => (cardsRef.current[index] = el)}
              data-index={index}
              className={`${visibleCards.includes(index) ? 'animate-swing-in' : 'opacity-0'}`}
              style={{ animationDelay: `${index * 0.1}s` }}
            >
              <Card 
                className="group relative overflow-hidden bg-gradient-to-br from-[hsl(186,70%,12%)] to-[hsl(186,60%,8%)] border border-white/10 hover:border-turquoise/50 transition-all duration-500 hover:animate-hanging cursor-pointer shadow-2xl hover:shadow-turquoise/20"
              >
                {/* Geometric pattern overlay */}
                <div className="absolute top-0 right-0 w-32 h-32 opacity-5 group-hover:opacity-10 transition-opacity duration-500">
                  {audience.pattern === "diagonal-lines" && (
                    <svg className="w-full h-full" viewBox="0 0 100 100">
                      <pattern id={`diagonal-${index}`} x="0" y="0" width="10" height="10" patternUnits="userSpaceOnUse">
                        <line x1="0" y1="0" x2="10" y2="10" stroke="white" strokeWidth="1"/>
                      </pattern>
                      <rect width="100" height="100" fill={`url(#diagonal-${index})`}/>
                    </svg>
                  )}
                  {audience.pattern === "dots" && (
                    <svg className="w-full h-full" viewBox="0 0 100 100">
                      <pattern id={`dots-${index}`} x="0" y="0" width="10" height="10" patternUnits="userSpaceOnUse">
                        <circle cx="5" cy="5" r="1.5" fill="white"/>
                      </pattern>
                      <rect width="100" height="100" fill={`url(#dots-${index})`}/>
                    </svg>
                  )}
                  {audience.pattern === "grid" && (
                    <svg className="w-full h-full" viewBox="0 0 100 100">
                      <pattern id={`grid-${index}`} x="0" y="0" width="10" height="10" patternUnits="userSpaceOnUse">
                        <path d="M 10 0 L 0 0 0 10" fill="none" stroke="white" strokeWidth="0.5"/>
                      </pattern>
                      <rect width="100" height="100" fill={`url(#grid-${index})`}/>
                    </svg>
                  )}
                  {audience.pattern === "circles" && (
                    <svg className="w-full h-full" viewBox="0 0 100 100">
                      <pattern id={`circles-${index}`} x="0" y="0" width="20" height="20" patternUnits="userSpaceOnUse">
                        <circle cx="10" cy="10" r="8" fill="none" stroke="white" strokeWidth="0.5"/>
                      </pattern>
                      <rect width="100" height="100" fill={`url(#circles-${index})`}/>
                    </svg>
                  )}
                  {audience.pattern === "waves" && (
                    <svg className="w-full h-full" viewBox="0 0 100 100">
                      <pattern id={`waves-${index}`} x="0" y="0" width="20" height="10" patternUnits="userSpaceOnUse">
                        <path d="M0,5 Q5,0 10,5 T20,5" fill="none" stroke="white" strokeWidth="0.5"/>
                      </pattern>
                      <rect width="100" height="100" fill={`url(#waves-${index})`}/>
                    </svg>
                  )}
                  {audience.pattern === "triangles" && (
                    <svg className="w-full h-full" viewBox="0 0 100 100">
                      <pattern id={`triangles-${index}`} x="0" y="0" width="10" height="10" patternUnits="userSpaceOnUse">
                        <polygon points="5,0 10,10 0,10" fill="none" stroke="white" strokeWidth="0.5"/>
                      </pattern>
                      <rect width="100" height="100" fill={`url(#triangles-${index})`}/>
                    </svg>
                  )}
                </div>

                <CardContent className="p-5 md:p-7 relative">
                  {/* Number badge */}
                  <div className="absolute top-4 md:top-5 right-5 md:right-7 w-8 h-8 rounded-full bg-gradient-to-br from-white/5 to-white/10 flex items-center justify-center border border-white/10 group-hover:animate-shake backdrop-blur-sm group-hover:border-turquoise/50 transition-colors">
                    <span className="text-white/80 text-xs font-bold">{String(index + 1).padStart(2, '0')}</span>
                  </div>

                  {/* Icon with hexagon shape */}
                  <div className="relative w-14 h-14 md:w-16 md:h-16 mb-4 md:mb-5 group-hover:animate-shake">
                    <div className="absolute inset-0 bg-gradient-to-br from-turquoise/20 to-maroon/20 clip-hexagon"></div>
                    <div className="absolute inset-0 flex items-center justify-center">
                      <audience.icon className="w-6 h-6 md:w-7 md:h-7 text-white relative z-10 drop-shadow" strokeWidth={1.5} />
                    </div>
                  </div>

                  {/* Content */}
                  <h3 className="font-heading text-lg md:text-xl text-white mb-2 md:mb-3 tracking-tight leading-tight font-semibold">
                    {audience.title}
                  </h3>
                  <p className="text-gray-300 text-xs md:text-sm leading-relaxed">
                    {audience.description}
                  </p>

                  {/* Corner accent */}
                  <div className="absolute bottom-0 left-0 w-10 h-10 md:w-12 md:h-12 border-l-2 border-b-2 border-turquoise/30 group-hover:border-turquoise/60 transition-colors duration-500"></div>
                </CardContent>
              </Card>
            </div>
          ))}
        </div>

        {/* Bottom text */}
        <div className="text-center mt-8 md:mt-12">
          <p className="text-gray-400 text-xs md:text-sm">
            Join hundreds of Kenyans transforming their digital marketing careers
          </p>
        </div>
      </div>

      <style>{`
        .bg-gradient-radial {
          background: radial-gradient(circle, var(--tw-gradient-stops));
        }

        .clip-hexagon {
          clip-path: polygon(30% 0%, 70% 0%, 100% 50%, 70% 100%, 30% 100%, 0% 50%);
        }

        @keyframes swing-in {
          0% {
            opacity: 0;
            transform: translateY(20px) rotate(-2deg);
          }
          60% {
            transform: translateY(-5px) rotate(1deg);
          }
          100% {
            opacity: 1;
            transform: translateY(0) rotate(0deg);
          }
        }

        @keyframes hanging {
          0%, 100% {
            transform: rotate(0deg) translateY(0);
            transform-origin: top center;
          }
          10% {
            transform: rotate(3deg) translateY(-2px);
          }
          30% {
            transform: rotate(-3deg) translateY(-1px);
          }
          50% {
            transform: rotate(2deg) translateY(-2px);
          }
          70% {
            transform: rotate(-2deg) translateY(-1px);
          }
          90% {
            transform: rotate(1deg) translateY(-1px);
          }
        }

        @keyframes shake {
          0%, 100% {
            transform: rotate(0deg) scale(1);
          }
          10%, 30%, 50%, 70%, 90% {
            transform: rotate(-4deg) scale(1.05);
          }
          20%, 40%, 60%, 80% {
            transform: rotate(4deg) scale(1.05);
          }
        }

        .animate-swing-in {
          animation: swing-in 0.8s cubic-bezier(0.34, 1.56, 0.64, 1) forwards;
        }

        .animate-hanging {
          animation: hanging 1.5s ease-in-out infinite;
        }

        .animate-shake {
          animation: shake 0.6s ease-in-out;
        }
      `}</style>
    </section>
  );
}